import * as React from 'react';

import { Listbox } from '@headlessui/react';
import { c } from 'ttag';

import { Check } from '@freelancelabs/icons';
import { clsx } from '@freelancelabs/utils';

import { Popper, PopperPlacement, PopperPosition, usePopper } from '../popper';

export interface SelectOption<T> {
    label: string;
    disabled?: boolean;
    /**
     * Why this option cannot be picked. Same reading as the tree's `disabledReason`,
     * and for the same reason: an option that goes grey without a word reads as a
     * broken control, and the operator cannot tell whether the list is wrong or their
     * own rights are. Rendered beside the label, not only announced.
     *
     * It answers « pourquoi celui-là », so keep it to the qualifier: the rule behind
     * a whole family of refusals belongs next to the field, said once.
     */
    disabledReason?: React.ReactNode;
    value: T;
}

interface Props<T> {
    options: SelectOption<T>[];
    anchorRef: React.RefObject<HTMLElement | null>;
    anchorPosition?: PopperPosition | null;
    originalPlacement?: PopperPlacement;
    isOpen?: boolean;
    /**
     * What the list says when it holds no option. Say *why* it is empty whenever the
     * screen knows: « still loading », « nothing left to add », « could not be
     * loaded »: the generic wording is only a fallback.
     */
    emptyLabel?: React.ReactNode;
}

const SelectOptions = <T,>({
    options,
    isOpen,
    anchorPosition,
    anchorRef,
    originalPlacement = 'bottom',
    emptyLabel,
}: Props<T>) => {
    const { floating, position, arrow, placement } = usePopper({
        reference:
            anchorPosition || anchorPosition === null
                ? {
                      mode: 'position',
                      value: anchorPosition,
                      anchor: anchorRef.current,
                  }
                : {
                      mode: 'element',
                      value: anchorRef.current,
                  },
        isOpen,
        originalPlacement,
    });
    return (
        <>
            {isOpen && (
                <Popper
                    isOpen
                    divRef={floating}
                    style={{ ...position, ...arrow, width: `${anchorRef.current?.offsetWidth || 0}px` }}
                    className={clsx('dropdown', `dropdown--${placement}`)}
                    role="dialog"
                >
                    <Listbox.Options className="list">
                        {/* An empty list used to open as a blank sliver a few pixels high:
                            nothing on screen, nothing in the DOM, which reads as a broken
                            control rather than as an empty one. */}
                        {options.length === 0 && (
                            <li className="list-item is-disabled" role="presentation">
                                {emptyLabel ?? c('Select').t`No option available`}
                            </li>
                        )}
                        {options.map((option, i) => {
                            const key = i.toString();
                            return (
                                <Listbox.Option key={key} value={option} disabled={option.disabled} as={React.Fragment}>
                                    {({ active, selected, disabled }) => (
                                        <li
                                            className={clsx(
                                                'list-item',
                                                active && 'is-active',
                                                selected && 'is-checked',
                                                disabled && 'is-disabled'
                                            )}
                                        >
                                            {option.label}
                                            {/* Left as a sibling of the bare label rather than
                                                wrapping it: every existing Select keeps its
                                                markup, and the reason takes the free space
                                                before the check with its own auto margin. */}
                                            {option.disabledReason && (
                                                <span className="list-item-reason">{option.disabledReason}</span>
                                            )}
                                            <div className="list-item-check">{selected && <Check />}</div>
                                        </li>
                                    )}
                                </Listbox.Option>
                            );
                        })}
                    </Listbox.Options>
                </Popper>
            )}
        </>
    );
};

export default SelectOptions;
